import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ChevronDown, 
  ChevronUp, 
  Trash2, 
  X, 
  Save, 
  PlusCircle,
  Plus,
  Minus,
  Copy
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  getCourses, 
  updateCourse,
  addModuleToCourse,
  updateModuleInCourse,
  deleteModuleFromCourse
} from '@/lib/firebase/queries/courses';
import type { Course, CourseModule, InteractiveContent, QuizContent, QuizQuestion } from '@/components/education/types';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

interface AdminModuleManagerProps {
  course: Course;
  onClose: () => void;
}

export function AdminModuleManager({ course: initialCourse, onClose }: AdminModuleManagerProps) {
  const { t } = useLanguage();
  const [course, setCourse] = useState<Course>(initialCourse);
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(null);
  const [moduleForm, setModuleForm] = useState<Partial<CourseModule>>({
    id: uuidv4(),
    title: '',
    duration: '',
    type: 'video'
  });
  const [interactiveTexts, setInteractiveTexts] = useState<string[]>(['']);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([{
    id: uuidv4(),
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: ''
  }]);
  const [passingScore, setPassingScore] = useState(70);
  const [allowRetry, setAllowRetry] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load fresh course data
  useEffect(() => {
    const loadCourse = async () => {
      try {
        const courses = await getCourses();
        const updatedCourse = courses.find(c => c.id === initialCourse.id);
        if (updatedCourse) {
          setCourse(updatedCourse);
        }
      } catch (error) {
        console.error('Error loading course:', error);
      }
    };
    loadCourse();
  }, [initialCourse.id]);

  // Auto-resize textareas when interactive texts change
  useEffect(() => {
    const textareas = document.querySelectorAll('textarea[placeholder*="Interactive text"]');
    textareas.forEach((textarea) => {
      const element = textarea as HTMLTextAreaElement;
      element.style.height = 'auto';
      element.style.height = element.scrollHeight + 'px';
    });
  }, [interactiveTexts]);

  const handleSelectModule = (module: CourseModule) => {
    setSelectedModule(module);
    setModuleForm(module);
    
    // Load module-specific content
    if (module.type === 'interactive' && module.interactiveContent) {
      setInteractiveTexts(module.interactiveContent.texts);
    } else {
      setInteractiveTexts(['']);
    }
    
    if (module.type === 'quiz' && module.quizContent) {
      setQuizQuestions(module.quizContent.questions);
      setPassingScore(module.quizContent.passingScore);
      setAllowRetry(module.quizContent.allowRetry);
    } else {
      setQuizQuestions([{
        id: uuidv4(),
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: ''
      }]);
      setPassingScore(70);
      setAllowRetry(true);
    }
  };

  const handleAddModule = () => {
    setSelectedModule(null);
    setModuleForm({
      id: uuidv4(),
      title: '',
      duration: '',
      type: 'video'
    });
    setInteractiveTexts(['']);
    setQuizQuestions([{
      id: uuidv4(),
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: ''
    }]);
  };

  const handleSaveModule = async () => {
    if (!moduleForm.title || !moduleForm.duration) {
      toast.error('Module title and duration are required');
      return;
    }

    try {
      setSaving(true);
      
      // Prepare module data
      const moduleData: CourseModule = {
        id: moduleForm.id || uuidv4(),
        title: moduleForm.title,
        duration: moduleForm.duration,
        type: moduleForm.type || 'video',
        completed: false,
        content: moduleForm.content || ''
      };

      // Add type-specific content
      if (moduleForm.type === 'interactive') {
        const validTexts = interactiveTexts.filter(text => text.trim() !== '');
        if (validTexts.length === 0) {
          toast.error('Interactive content is required');
          return;
        }
        moduleData.interactiveContent = { texts: validTexts };
      } else if (moduleForm.type === 'quiz') {
        const validQuestions = quizQuestions.filter(q => 
          q.question.trim() !== '' && q.options.some(opt => opt.trim() !== '')
        );
        if (validQuestions.length === 0) {
          toast.error('At least one quiz question is required');
          return;
        }
        moduleData.quizContent = {
          questions: validQuestions,
          passingScore,
          allowRetry
        };
      }

      if (selectedModule) {
        // Update existing module
        await updateModuleInCourse(course.id, moduleData.id, moduleData);
        setCourse(prev => ({
          ...prev,
          modules: prev.modules?.map(m => m.id === moduleData.id ? moduleData : m) || []
        }));
        toast.success('Module updated successfully');
      } else {
        // Add new module
        await addModuleToCourse(course.id, moduleData);
        setCourse(prev => ({
          ...prev,
          modules: [...(prev.modules || []), moduleData]
        }));
        toast.success('Module added successfully');
      }

      // Reset form
      setSelectedModule(null);
      setModuleForm({
        id: uuidv4(),
        title: '',
        duration: '',
        type: 'video'
      });
      setInteractiveTexts(['']);
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error('Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (moduleId: string) => {
    try {
      setSaving(true);
      await deleteModuleFromCourse(course.id, moduleId);
      setCourse(prev => ({
        ...prev,
        modules: prev.modules?.filter(m => m.id !== moduleId) || []
      }));
      
      if (selectedModule?.id === moduleId) {
        setSelectedModule(null);
        setModuleForm({
          id: uuidv4(),
          title: '',
          duration: '',
          type: 'video'
        });
      }
      
      toast.success('Module deleted successfully');
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Failed to delete module');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveModule = async (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= (course.modules?.length || 0)) return;
    
    try {
      setSaving(true);
      const newModules = [...(course.modules || [])];
      const [movedModule] = newModules.splice(fromIndex, 1);
      newModules.splice(toIndex, 0, movedModule);
      
      const updatedCourse = { ...course, modules: newModules };
      await updateCourse(course.id, { modules: newModules });
      setCourse(updatedCourse);
      
      toast.success('Module order updated');
    } catch (error) {
      console.error('Error moving module:', error);
      toast.error('Failed to update module order');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateModule = async (moduleIndex: number) => {
    try {
      setSaving(true);
      const originalModule = course.modules?.[moduleIndex];
      if (!originalModule) return;

      // Create a duplicate with new ID and modified title
      const duplicatedModule: CourseModule = {
        ...originalModule,
        id: uuidv4(),
        title: `${originalModule.title} (Copy)`,
        completed: false
      };

      // Insert the duplicate right after the original
      const newModules = [...(course.modules || [])];
      newModules.splice(moduleIndex + 1, 0, duplicatedModule);
      
      await addModuleToCourse(course.id, duplicatedModule);
      setCourse(prev => ({
        ...prev,
        modules: newModules
      }));
      
      toast.success('Module duplicated successfully');
    } catch (error) {
      console.error('Error duplicating module:', error);
      toast.error('Failed to duplicate module');
    } finally {
      setSaving(false);
    }
  };

  const addInteractiveText = () => {
    setInteractiveTexts([...interactiveTexts, '']);
  };

  const removeInteractiveText = (index: number) => {
    setInteractiveTexts(interactiveTexts.filter((_, i) => i !== index));
  };

  const updateInteractiveText = (index: number, value: string) => {
    const newTexts = [...interactiveTexts];
    newTexts[index] = value;
    setInteractiveTexts(newTexts);
  };

  const addQuizQuestion = () => {
    setQuizQuestions([...quizQuestions, {
      id: uuidv4(),
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: ''
    }]);
  };

  const removeQuizQuestion = (index: number) => {
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  const updateQuizQuestion = (index: number, field: keyof QuizQuestion, value: any) => {
    const newQuestions = [...quizQuestions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuizQuestions(newQuestions);
  };

  const updateQuizOption = (questionIndex: number, optionIndex: number, value: string) => {
    const newQuestions = [...quizQuestions];
    newQuestions[questionIndex].options[optionIndex] = value;
    setQuizQuestions(newQuestions);
  };

  return (
    <div className="flex h-[70vh] bg-background rounded-lg overflow-hidden">
      {/* Sidebar */}
      <div className="w-[28rem] border-r bg-muted flex flex-col rounded-l-lg">
        <div className="p-4 border-b bg-muted/50 rounded-tl-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Course Modules</h3>
            <Badge variant="secondary">
              {course.modules?.length || 0} modules
            </Badge>
          </div>
        </div>

        <ScrollArea className="flex-1 p-4">
          <div className="space-y-2">
            {(course.modules || []).map((module, moduleIndex) => (
              <div
                key={module.id}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedModule?.id === module.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-accent'
                }`}
                onClick={() => handleSelectModule(module)}
              >
                 <div className="flex items-center justify-between">
                   {/* Far Left: Movement Controls */}
                   <div className="flex items-center gap-2">
                     {selectedModule?.id === module.id && (
                       <div className="flex flex-col">
                         {/* Move Up Button */}
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                           onClick={(e) => {
                             e.stopPropagation();
                             handleMoveModule(moduleIndex, moduleIndex - 1);
                           }}
                           disabled={moduleIndex === 0 || saving}
                           title="Move Up"
                         >
                           <ChevronUp className="h-4 w-4" />
                         </Button>
                         
                         {/* Move Down Button */}
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                           onClick={(e) => {
                             e.stopPropagation();
                             handleMoveModule(moduleIndex, moduleIndex + 1);
                           }}
                           disabled={moduleIndex === (course.modules?.length || 0) - 1 || saving}
                           title="Move Down"
                         >
                           <ChevronDown className="h-4 w-4" />
                         </Button>
                       </div>
                     )}
                   </div>

                   {/* Center: Module Content */}
                   <div className="flex-1 px-4">
                     <h4 className="font-medium">{module.title}</h4>
                     <p className="text-sm opacity-80">
                       {module.duration} • {module.type}
                     </p>
                   </div>

                   {/* Far Right: Action Controls */}
                   <div className="flex items-center gap-2">
                     {selectedModule?.id === module.id && (
                       <div className="flex flex-col">
                         {/* Duplicate Button */}
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                           onClick={(e) => {
                             e.stopPropagation();
                             handleDuplicateModule(moduleIndex);
                           }}
                           disabled={saving}
                           title="Duplicate Module"
                         >
                           <Copy className="h-4 w-4" />
                         </Button>
                         
                         {/* Delete Button */}
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                           onClick={(e) => {
                             e.stopPropagation();
                             handleDeleteModule(module.id);
                           }}
                           disabled={saving}
                           title="Delete Module"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     )}
                   </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="p-4 border-t rounded-bl-lg">
          <Button className="w-full" onClick={handleAddModule}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Add New Module
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto rounded-r-lg">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                {selectedModule ? 'Edit Module' : 'Add New Module'}
              </h3>
              <p className="text-muted-foreground">
                {selectedModule ? 'Modify the selected module' : 'Create a new module for this course'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Basic Module Info */}
            <div className="grid gap-6">
              <div className="space-y-2">
                <Label htmlFor="title">Module Title</Label>
                <Input
                  id="title"
                  value={moduleForm.title || ''}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  placeholder="Enter module title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={moduleForm.duration || ''}
                    onChange={(e) => setModuleForm({ ...moduleForm, duration: e.target.value })}
                    placeholder="e.g., 30 minutes"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Module Type</Label>
                  <Select
                    value={moduleForm.type}
                    onValueChange={(value: 'video' | 'reading' | 'quiz' | 'interactive') => {
                      setModuleForm({ ...moduleForm, type: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select module type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="reading">Reading</SelectItem>
                      <SelectItem value="quiz">Quiz</SelectItem>
                      <SelectItem value="interactive">Interactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Content based on module type */}
              {moduleForm.type === 'video' && (
                <div className="space-y-2">
                  <Label htmlFor="content">Video URL or Content</Label>
                  <Textarea
                    id="content"
                    value={moduleForm.content || ''}
                    onChange={(e) => setModuleForm({ ...moduleForm, content: e.target.value })}
                    placeholder="Enter video URL or content description"
                    rows={3}
                  />
                </div>
              )}

              {moduleForm.type === 'reading' && (
                <div className="space-y-2">
                  <Label htmlFor="content">Reading Content</Label>
                  <Textarea
                    id="content"
                    value={moduleForm.content || ''}
                    onChange={(e) => setModuleForm({ ...moduleForm, content: e.target.value })}
                    placeholder="Enter reading content or instructions"
                    rows={5}
                  />
                </div>
              )}

              {moduleForm.type === 'interactive' && (
                <div className="space-y-4">
                  <Label>Interactive Texts</Label>
                  {interactiveTexts.map((text, index) => (
                    <div key={index} className="flex gap-2">
                      <Textarea
                        value={text}
                        onChange={(e) => updateInteractiveText(index, e.target.value)}
                        placeholder={`Interactive text ${index + 1}`}
                        className="min-h-[40px] resize-none"
                        rows={1}
                        style={{
                          height: 'auto',
                          minHeight: '40px'
                        }}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = 'auto';
                          target.style.height = target.scrollHeight + 'px';
                        }}
                      />
                      {interactiveTexts.length > 1 && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeInteractiveText(index)}
                          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    onClick={addInteractiveText}
                    className="border-green-200 text-green-600 hover:bg-green-50 hover:text-green-700 hover:border-green-300"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Text
                  </Button>
                </div>
              )}

              {moduleForm.type === 'quiz' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="passingScore">Passing Score (%)</Label>
                      <Input
                        id="passingScore"
                        type="number"
                        min="0"
                        max="100"
                        value={passingScore}
                        onChange={(e) => setPassingScore(Number(e.target.value))}
                      />
                    </div>
                    <div className="flex items-center space-x-2 pt-6">
                      <input
                        type="checkbox"
                        id="allowRetry"
                        checked={allowRetry}
                        onChange={(e) => setAllowRetry(e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="allowRetry">Allow Retry</Label>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Label>Quiz Questions</Label>
                    {quizQuestions.map((question, qIndex) => (
                      <Card key={question.id}>
                        <CardContent className="pt-6">
                          <div className="space-y-4">
                            <div className="flex gap-2">
                              <Input
                                value={question.question}
                                onChange={(e) => updateQuizQuestion(qIndex, 'question', e.target.value)}
                                placeholder={`Question ${qIndex + 1}`}
                              />
                              {quizQuestions.length > 1 && (
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => removeQuizQuestion(qIndex)}
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              {question.options.map((option, oIndex) => (
                                <div key={oIndex} className="flex gap-2">
                                  <input
                                    type="radio"
                                    name={`correct-${qIndex}`}
                                    checked={question.correctAnswer === oIndex}
                                    onChange={() => updateQuizQuestion(qIndex, 'correctAnswer', oIndex)}
                                  />
                                  <Input
                                    value={option}
                                    onChange={(e) => updateQuizOption(qIndex, oIndex, e.target.value)}
                                    placeholder={`Option ${oIndex + 1}`}
                                  />
                                </div>
                              ))}
                            </div>
                            
                            <Input
                              value={question.explanation}
                              onChange={(e) => updateQuizQuestion(qIndex, 'explanation', e.target.value)}
                              placeholder="Explanation (optional)"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    <Button variant="outline" onClick={addQuizQuestion}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Question
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleSaveModule} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : selectedModule ? 'Update Module' : 'Add Module'}
              </Button>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
